import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../components-v2/Theme/theme';

// =============================================================================
// Layout Constants
// =============================================================================
const LAYOUT_MAX_WIDTH = 1500;
const COLUMN_MAX_WIDTH_LEFT = 700;
const COLUMN_MAX_WIDTH_RIGHT = 700;
const SUCCESS_SECTION_MAX_WIDTH = 600;
const WHAT_NEXT_SECTION_MAX_WIDTH = 600;
const WHAT_NEXT_SECTION_MIN_WIDTH = 320;

// =============================================================================
// Icon & Image Dimensions
// =============================================================================
const ICON_SIZE_SMALL = 20;
const ICON_SIZE_MEDIUM = 48;
const ICON_SIZE_CARD = 72;
const ICON_SIZE_PRIMARY = 240;
const PRIMARY_ICON_CONTAINER_WIDTH = 320;
const PRIMARY_ICON_CONTAINER_HEIGHT = 200;

// =============================================================================
// Card Dimensions
// =============================================================================
const CARD_MIN_HEIGHT = 180;
const CARD_BORDER_RADIUS = 12;
const SMALL_BORDER_RADIUS = 8;

// =============================================================================
// Opacity Values (hex notation for consistency)
// =============================================================================
const OPACITY_10 = '1A'; // 10%
const OPACITY_20 = '33'; // 20%

// =============================================================================
// Special Colors
// =============================================================================

// =============================================================================
// Base Styles for Composition
// =============================================================================
const baseCard = {
	backgroundColor: THEME.colors.background.light,
	border: `1px solid ${THEME.colors.border.light}`,
	borderRadius: CARD_BORDER_RADIUS,
	boxShadow: `0 2px 8px ${THEME.colors.border.light}${OPACITY_20}`,
};

const baseIconContainer = {
	display: 'flex' as const,
	alignItems: 'center' as const,
	justifyContent: 'center' as const,
	borderRadius: CARD_BORDER_RADIUS,
	overflow: 'hidden' as const,
	flexShrink: 0,
};

export const s = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: `${THEME.spacing.md}px ${THEME.spacing.xl}px`,
		backgroundColor: THEME.colors.background.light,
		textAlign: 'center',
		gap: THEME.spacing.lg,
		overflow: 'hidden',
	},

	mainContent: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: THEME.spacing.xxxl,
		width: '100%',
		maxWidth: LAYOUT_MAX_WIDTH,
		[THEME.queries.lg]: {
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
		},
	},

	successSection: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.lg,
		flex: '1 1 0',
		maxWidth: SUCCESS_SECTION_MAX_WIDTH,
		[THEME.queries.lg]: {
			width: '100%',
			flex: 'none',
		},
	},

	whatNextSection: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.lg,
		flex: '1 1 0',
		maxWidth: WHAT_NEXT_SECTION_MAX_WIDTH,
		minWidth: WHAT_NEXT_SECTION_MIN_WIDTH,
		[THEME.queries.lg]: {
			width: '100%',
			flex: 'none',
			alignItems: 'center',
			maxWidth: 800,
		},
	},

	whatNextTitle: {
		fontSize: THEME.typography.fontSizes.xxl,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.primary.main,
		marginBottom: THEME.spacing.sm,
		textAlign: 'center',
	},

	iconContainer: {
		position: 'relative',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		marginBottom: THEME.spacing.sm,
	},

	icon: {
		width: ICON_SIZE_MEDIUM,
		height: ICON_SIZE_MEDIUM,
		color: THEME.colors.primary.main,
	},

	successIcon: {
		position: 'absolute',
		bottom: -THEME.spacing.sm,
		right: -THEME.spacing.sm,
		width: ICON_SIZE_SMALL,
		height: ICON_SIZE_SMALL,
		backgroundColor: THEME.colors.success.main,
		borderRadius: '50%',
		padding: THEME.spacing.xs,
		color: THEME.colors.success.contrastText,
	},

	titleMessage: {
		fontSize: THEME.typography.fontSizes.lg,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		marginBottom: THEME.spacing.sm,
		lineHeight: 1.4,
	},

	detailMessage: {
		fontSize: THEME.typography.fontSizes.md,
		fontFamily: THEME.typography.fontFamilies.lexend,
		color: THEME.colors.text.light,
		lineHeight: 1.5,
		maxWidth: SUCCESS_SECTION_MAX_WIDTH,
	},

	whoWillBeNotified: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
		fontStyle: 'italic',
		marginBottom: THEME.spacing.sm,
	},

	link: {
		color: THEME.colors.primary.main,
		textDecoration: 'none',
		fontWeight: THEME.typography.fontWeights.bold,
		':hover': {
			textDecoration: 'underline',
		},
	},

	closeButton: {
		marginTop: THEME.spacing.lg,
	},

	aiCardsContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		justifyContent: 'flex-start',
		alignItems: 'stretch',
		width: '100%',
		[THEME.queries.lg]: {
			alignItems: 'center',
			gap: THEME.spacing.lg,
		},
	},

	aiCard: {
		...baseCard,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: THEME.spacing.xl,
		gap: THEME.spacing.md,
		width: '100%',
		minHeight: CARD_MIN_HEIGHT,
		transition: 'box-shadow 0.2s ease, transform 0.2s ease',
		':hover': {
			boxShadow: `0 4px 16px ${THEME.colors.border.light}40`,
			transform: 'translateY(-1px)',
		},
		[THEME.queries.lg]: {
			maxWidth: WHAT_NEXT_SECTION_MIN_WIDTH,
		},
	},

	aiCardRow: {
		...baseCard,
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		padding: THEME.spacing.lg,
		gap: THEME.spacing.lg,
		width: '100%',
		transition: 'box-shadow 0.2s ease, transform 0.2s ease',
		':hover': {
			boxShadow: `0 4px 16px ${THEME.colors.border.light}40`,
			transform: 'translateY(-1px)',
		},
	},

	aiCardContent: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		flex: 1,
		gap: THEME.spacing.sm,
	},

	aiCardRowIcon: {
		...baseIconContainer,
		width: 120,
		height: 120,
		color: THEME.colors.background.main,
	},

	aiCardRowImage: {
		width: 72,
		height: 72,
		objectFit: 'contain',
	},

	aiCardIcon: {
		width: ICON_SIZE_CARD,
		height: ICON_SIZE_CARD,
		padding: THEME.spacing.md,
		backgroundColor: THEME.colors.primary.light,
		borderRadius: CARD_BORDER_RADIUS,
		color: THEME.colors.primary.main,
		marginBottom: THEME.spacing.sm,
	},

	aiCardIconContainer: {
		...baseIconContainer,
		width: ICON_SIZE_CARD,
		height: ICON_SIZE_CARD,
		marginBottom: THEME.spacing.sm,
		boxShadow: `0 2px 8px ${THEME.colors.border.main}${OPACITY_20}`,
	},

	// Primary resource icon container
	primaryIconContainer: {
		...baseIconContainer,
		width: PRIMARY_ICON_CONTAINER_WIDTH,
		height: PRIMARY_ICON_CONTAINER_HEIGHT,
		marginBottom: THEME.spacing.sm,
		boxShadow: `0 2px 8px ${THEME.colors.border.main}${OPACITY_20}`,
	},

	// Background colors for icon containers
	blogIconBackground: {
		backgroundColor: 'rgba(139, 61, 198, 0.16)',
	},

	socialIconBackground: {
		backgroundColor: 'rgba(0, 204, 255, 0.1)',
	},

	emailIconBackground: {
		backgroundColor: 'rgba(224, 114, 190, 0.21)',
	},

	// Primary display backgrounds (for large icon containers)
	primaryResourceIconBackground: {
		backgroundColor: THEME.colors.success.light,
	},

	aiCardImage: {
		width: '100%',
		height: 'auto',
		display: 'block',
		borderRadius: 'inherit',
	},

	aiCardTitle: {
		fontSize: THEME.typography.fontSizes.lg,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		marginBottom: THEME.spacing.sm,
		textAlign: 'left',
	},

	aiCardDescription: {
		fontSize: THEME.typography.fontSizes.md,
		fontFamily: THEME.typography.fontFamilies.lexend,
		color: THEME.colors.text.light,
		textAlign: 'left',
		lineHeight: 1.5,
		marginBottom: THEME.spacing.md,
		flex: 1,
	},

	aiCardButtonContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		width: '100%',
		marginTop: THEME.spacing.sm,
	},

	newBadge: {
		fontSize: THEME.typography.fontSizes.xxs,
		backgroundColor: THEME.colors.success.main,
		color: THEME.colors.success.contrastText,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
		borderRadius: CARD_BORDER_RADIUS,
		fontWeight: THEME.typography.fontWeights.bold,
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
	},

	// =============================================================================
	// Multi-Channel Layout
	// =============================================================================
	multiChannelContainer: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: THEME.spacing.xl,
		width: '100%',
		maxWidth: LAYOUT_MAX_WIDTH,
		[THEME.queries.lg]: {
			flexDirection: 'column',
			alignItems: 'center',
			gap: THEME.spacing.lg,
		},
	},

	leftSide: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.lg,
		flex: '1 1 0',
		maxWidth: COLUMN_MAX_WIDTH_LEFT,
		[THEME.queries.lg]: {
			width: '100%',
			flex: 'none',
		},
	},

	successCallout: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		textAlign: 'center',
		maxWidth: COLUMN_MAX_WIDTH_LEFT,
		padding: THEME.spacing.lg,
		borderRadius: CARD_BORDER_RADIUS,
	},

	successHeading: {
		fontSize: THEME.typography.fontSizes.xxl,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.success.main,
		marginBottom: THEME.spacing.xs,
	},

	rightSide: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.lg,
		flex: '1 1 0',
		maxWidth: COLUMN_MAX_WIDTH_RIGHT,
		minWidth: WHAT_NEXT_SECTION_MIN_WIDTH,
		paddingRight: THEME.spacing.lg,
		[THEME.queries.lg]: {
			width: '100%',
			flex: 'none',
			paddingRight: 0,
		},
	},

	singleResourceLayout: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.lg,
		width: '100%',
		maxWidth: COLUMN_MAX_WIDTH_LEFT,
	},

	sectionTitle: {
		fontSize: THEME.typography.fontSizes.xl,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		marginBottom: THEME.spacing.md,
		textAlign: 'left',
		[THEME.queries.lg]: {
			textAlign: 'center',
		},
	},

	completedResource: {
		display: 'flex',
		flexDirection: 'column',
		padding: THEME.spacing.xl,
		width: '100%',
		marginBottom: THEME.spacing.md,
	},

	primaryResourceContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: THEME.spacing.lg,
	},

	primaryResourceIcon: {
		...baseIconContainer,
		width: PRIMARY_ICON_CONTAINER_WIDTH,
		height: PRIMARY_ICON_CONTAINER_HEIGHT,
		marginTop: THEME.spacing.lg,
		marginBottom: THEME.spacing.lg,
	},

	primaryResourceImage: {
		width: ICON_SIZE_PRIMARY,
		height: ICON_SIZE_PRIMARY,
		objectFit: 'contain',
	},

	primaryResourceText: {
		fontSize: THEME.typography.fontSizes.md,
		fontFamily: THEME.typography.fontFamilies.lexend,
		color: THEME.colors.text.main,
		textAlign: 'center',
		lineHeight: 1.5,
	},

	resourceHeader: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: THEME.spacing.md,
		marginBottom: THEME.spacing.md,
	},

	resourceIcon: {
		width: ICON_SIZE_MEDIUM,
		height: ICON_SIZE_MEDIUM,
		padding: THEME.spacing.sm,
		backgroundColor: THEME.colors.primary.light,
		borderRadius: SMALL_BORDER_RADIUS,
		color: THEME.colors.primary.main,
		flexShrink: 0,
	},

	resourceInfo: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		flex: 1,
	},

	resourceTitle: {
		fontSize: THEME.typography.fontSizes.lg,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		margin: 0,
	},

	resourceStatus: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.success.main,
	},

	statusIcon: {
		width: ICON_SIZE_SMALL,
		height: ICON_SIZE_SMALL,
		color: THEME.colors.success.main,
	},

	resourceMetadata: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		paddingTop: THEME.spacing.md,
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},

	metadataItem: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
		fontFamily: THEME.typography.fontFamilies.lexend,
		lineHeight: 1.4,
	},

	completedStatus: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		backgroundColor: THEME.colors.success.light + OPACITY_10,
		color: THEME.colors.success.main,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
		borderRadius: 16,
		fontWeight: THEME.typography.fontWeights.bold,
		fontFamily: THEME.typography.fontFamilies.lexend,
		fontSize: THEME.typography.fontSizes.sm,
		marginTop: THEME.spacing.sm,
	},

	completedStatusIcon: {
		width: ICON_SIZE_SMALL,
		height: ICON_SIZE_SMALL,
		color: THEME.colors.success.main,
	},
});
